import { isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  HostBinding,
  PLATFORM_ID,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { ListItemType } from '@freelancer/ui/list-item';
import { FontColor } from '@freelancer/ui/text';
import { UserAgent } from '@freelancer/user-agent';
import { LOGGED_OUT_NAV_HEIGHT } from 'app/navigation/navigation-logged-out-home/navigation-logged-out-home.types';
import type { HeroData } from './home-page-hero.types';

@Component({
  standalone: false,
  selector: 'app-home-page-hero',
  template: `
    <div
      class="Hero"
      flTrackingSection="HomepageSplash"
    >
      <app-hero-image-carousel
        class="CarouselSpace"
        [heroes]="heroes"
        [startPlaying]="errorDetected || isMobile"
        (indexChanged)="indexChangeImage($event)"
      >
      </app-hero-image-carousel>
      @if (isBrowser && !isMobile) {
        @if (!errorDetected) {
          <app-video-hero
            class="mobile:hide"
            [videoSrc]="heroesVideoSrc"
            (errorDetected)="errorDetected = true"
            (indexChanged)="indexChangeVideo($event)"
          >
          </app-video-hero>
        }
      }
      <div class="Hero-wrapper">
        <fl-container
          class="Hero-content"
          [size]="ContainerSize.DESKTOP_LARGE"
          [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
          [mobilePadding]="true"
        >
          <div class="Hero-content-main">
            <div class="Hero-content-text">
              <h1
                class="text-light text-size-inherit Hero-content-header mb-mid"
                i18n="Home Page Heading"
              >
                Hire the best freelancers for any job, online.
              </h1>

              <div class="Hero-content-subheading">
                <div
                  class="text-light text-body-20 desktop_xlarge:text-body-24 font-normal mb-mid"
                >
                  <fl-list
                    class="Hero-content-subheading-bullets"
                    [type]="ListItemType.UNORDERED"
                    [fontColor]="FontColor.INHERIT"
                  >
                    <fl-list-item
                      i18n="Text emphasizing the user base of Freelancer.com"
                    >
                      World's largest freelance marketplace
                    </fl-list-item>
                    <fl-list-item i18n="The range of jobs in Freelancer.com">
                      Any job you can possibly think of
                    </fl-list-item>
                    <fl-list-item
                      i18n="Potential benefits from using Freelancer.com"
                    >
                      Save up to 90% & get quotes for free
                    </fl-list-item>
                    <fl-list-item i18n="Releasing payments when a client wants">
                      Pay only when you're 100% happy
                    </fl-list-item>
                  </fl-list>
                </div>
              </div>
            </div>
            <div class="CallToActionButtons">
              <fl-button
                class="CallToActionButtons-button mb-small tablet:mb-none tablet:mr-small"
                i18n="Hire a Freelancer button text"
                [size]="ButtonSize.XLARGE"
                [color]="ButtonColor.PRIMARY"
                [link]="'/post-project'"
                [flTrackingLabel]="'RedirectToPPP'"
              >
                Hire a Freelancer
              </fl-button>

              <fl-button
                i18n="Earn Money Freelancing button text"
                [size]="ButtonSize.XLARGE"
                [color]="ButtonColor.TRANSPARENT_LIGHT"
                [link]="'/signup'"
                [flTrackingLabel]="'RedirectToSignup'"
              >
                Earn Money Freelancing
              </fl-button>
            </div>
          </div>
        </fl-container>

        <div
          class="Hero-content-description-container mobile_small:hide tablet:hide"
        >
          <p
            class="text-light text-body-16 Hero-content-description desktop:text-body-20 font-weight-inherit"
            i18n="NASA hero description"
            [class.IsShown]="this.heroes[currentIndex].id === 0"
          >
            This mobile app design cost $1500 USD and took 20 days
          </p>
          <p
            class="text-light text-body-16 Hero-content-description desktop:text-body-20 font-weight-inherit"
            i18n="Mobile App hero description"
            [class.IsShown]="this.heroes[currentIndex].id === 1"
          >
            This radiation shield design for NASA cost $500 USD and took 15 days
          </p>
          <p
            class="text-light text-body-16 Hero-content-description desktop:text-body-20 font-weight-inherit"
            i18n="House hero description"
            [class.IsShown]="this.heroes[currentIndex].id === 2"
          >
            This architectural design cost $500 USD and took 15 days
          </p>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./home-page-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageHeroComponent implements OnInit {
  private userAgent = inject(UserAgent);
  private platformId = inject(PLATFORM_ID);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ContainerSize = ContainerSize;
  FontColor = FontColor;
  ListItemType = ListItemType;

  @HostBinding('style.--logged-out-nav-height')
  loggedOutNavHeight = LOGGED_OUT_NAV_HEIGHT;

  heroes: readonly HeroData[];
  heroesVideoSrc: readonly string[];

  currentIndex = 0;
  errorDetected = false;
  isMobile = false;
  isBrowser = false;

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = this.userAgent.isMobileDevice();
      this.isBrowser = true;
    }

    this.heroes = [
      {
        id: 0,
        alt: 'Mobile App Design',
        mobileImage: 'home/hero/mobile-phone-mobile.jpg',
        tabletImage: 'home/hero/mobile-phone-675.jpg',
        desktopSmallImage: 'home/hero/mobile-phone-850.jpg',
        desktopLargeImage: 'home/hero/mobile-phone-1080.jpg',
        videoUrl: 'home/video/mobile-phone-v3.mp4',
      },
      {
        id: 1,
        alt: 'Radiation Shield Design for NASA',
        mobileImage: 'home/hero/nasa-mobile.jpg',
        tabletImage: 'home/hero/nasa-675.jpg',
        desktopSmallImage: 'home/hero/nasa-850.jpg',
        desktopLargeImage: 'home/hero/nasa-1080.jpg',
        videoUrl: 'home/video/nasa-v3.mp4',
      },
      {
        id: 2,
        alt: 'Architectural Design',
        mobileImage: 'home/hero/house-mobile.jpg',
        tabletImage: 'home/hero/house-675.jpg',
        desktopSmallImage: 'home/hero/house-850.jpg',
        desktopLargeImage: 'home/hero/house-1080.jpg',
        videoUrl: 'home/video/house-v3.mp4',
      },
      {
        id: 3,
        alt: 'AI Contest',
        mobileImage: 'home/hero/ai-contest-mobile.jpg',
        tabletImage: 'home/hero/ai-contest-675.jpg',
        desktopSmallImage: 'home/hero/ai-contest-850.jpg',
        desktopLargeImage: 'home/hero/ai-contest-1080.jpg',
        videoUrl: 'home/video/ai-contest-hero.mp4',
      },
    ];

    this.heroesVideoSrc = this.heroes.map(hero => hero.videoUrl);
  }

  indexChangeVideo($event: number): void {
    if (!this.errorDetected) {
      this.currentIndex = $event;
    }
  }

  indexChangeImage($event: number): void {
    if (this.errorDetected || this.isMobile) {
      this.currentIndex = $event;
    }
  }
}
